/**
 * Showing the beginning of a prompt in the log, instead of how long it was.
 *
 * "Prompt sent (145 characters)" tells somebody looking at a session nothing they wanted to know.
 * The opening words tell them what was asked for, which is the reason they opened the log.
 *
 * The text is never taken from the timeline: the server puts only the length there, on purpose, and
 * the text lives behind its own read rules — the developer and their managers, and only when the
 * organization keeps prompts. So the preview is matched, in the browser, against the prompts the
 * reader was allowed to fetch. A reader who may not see them sees the count, exactly as before.
 */

import type { SessionPrompt } from './types'

/**
 * How far apart a timeline entry and a prompt record may be and still be the same prompt.
 *
 * They come from one event, so they normally agree exactly. The tolerance covers a clock that
 * rounded and a row that was written a moment after the event it describes.
 */
const TOLERANCE_MS = 2_000

/** How much of a prompt fits on one line of the log before it stops helping. */
export const PREVIEW_LENGTH = 90

/**
 * The prompt a timeline entry is about, or undefined when the reader cannot see it.
 *
 * Remote commands are included: for their own developer they read like any other instruction, and
 * for anybody else the server already stripped the text, so there is nothing to match.
 */
export function promptFor(prompts: SessionPrompt[], occurredAt: string): SessionPrompt | undefined {
  const at = Date.parse(occurredAt)
  if (Number.isNaN(at)) return undefined

  let best: SessionPrompt | undefined
  let bestDistance = TOLERANCE_MS

  for (const prompt of prompts) {
    const distance = Math.abs(Date.parse(prompt.submittedAt) - at)
    if (distance <= bestDistance) {
      best = prompt
      bestDistance = distance
    }
  }

  return best?.text ? best : undefined
}

/**
 * The opening of a prompt, on one line.
 *
 * Prompts are typed over several lines; the log is one. Whitespace is collapsed so a prompt that
 * starts with a blank line does not preview as nothing, and the cut prefers a word boundary when
 * one is near enough that the result still reads.
 */
export function preview(text: string, max = PREVIEW_LENGTH): string {
  const flat = text.replace(/\s+/g, ' ').trim()
  if (flat.length <= max) return flat

  const cut = flat.slice(0, max)
  const space = cut.lastIndexOf(' ')

  // Only honour the word boundary when it is not so far back that it throws the line away.
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`
}

/** Everything the reader may read, from a prompts response that may say they may not. */
export function readablePrompts(data: { trigger?: SessionPrompt | null; followUps: SessionPrompt[] } | undefined): SessionPrompt[] {
  if (!data) return []
  return [...(data.trigger ? [data.trigger] : []), ...data.followUps]
}
